import i18n from '@/utils/i18n';
import React from 'react';
import { InboundDetailHeaderAssignedEmployee } from './inbound-detail-header-assigned-employee';
import { Size } from '@/components/avatar';
import { SmartOrderAssignDropdown } from './smart-order-assign-dropdown';
import { type StyleProp, TouchableOpacity, type ViewStyle } from 'react-native';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import type { OrderInboundAssignedEmployee } from './types';

export interface Props {
  assignedEmployee?: OrderInboundAssignedEmployee;
  avatarContainerStyle?: StyleProp<ViewStyle>;
  avatarSize?: Size;
  onAssignEmployee: (employeeUUID: string) => Promise<void>;
  // Only fires when the dropdown renders -- plain self-assign has no menu
  // to open.
  onOpenChange?: (isOpen: boolean) => void;
  onUnassignEmployee: () => void;
  // The parent owns the unassigned-state trigger entirely -- e.g. a text
  // button styled one way in the detail header, an icon-only button in the
  // order list row. Called with the self-assign / open-dropdown handler to
  // wire up.
  renderUnassignedTrigger: (onPress: () => void) => React.ReactElement;
  restaurantName: string | null;
  smartOrderUUID: string;
  supplierUUID: string;
}

export const OrderInboundsAssignedEmployee = ({
  assignedEmployee,
  avatarContainerStyle,
  restaurantName,
  smartOrderUUID,
  supplierUUID,
  renderUnassignedTrigger,
  onAssignEmployee,
  onOpenChange,
  onUnassignEmployee,
  avatarSize,
}: Props) => {
  const { smartOrderSettings, user } = useAuthenticatedUserContext();

  // No dropdown for reps who can't hand work to a colleague -- tapping
  // self-assigns/unassigns directly.
  if (!smartOrderSettings?.assign_to_others) {
    if (assignedEmployee) {
      return (
        <InboundDetailHeaderAssignedEmployee
          assignedEmployee={assignedEmployee}
          avatarSize={avatarSize}
          containerStyle={avatarContainerStyle}
          onUnassignEmployee={onUnassignEmployee}
        />
      );
    }

    return renderUnassignedTrigger(() => void onAssignEmployee(user.uuid));
  }

  return (
    <SmartOrderAssignDropdown
      onOpenChange={onOpenChange}
      onSelectEmployee={onAssignEmployee}
      restaurantName={restaurantName}
      smartOrderUUID={smartOrderUUID}
      supplierUUID={supplierUUID}
      trigger={(open) =>
        assignedEmployee ? (
          <InboundDetailHeaderAssignedEmployee
            assignedEmployee={assignedEmployee}
            avatarSize={avatarSize}
            containerStyle={avatarContainerStyle}
            onPressAvatar={open}
            onUnassignEmployee={onUnassignEmployee}
          />
        ) : (
          renderUnassignedTrigger(open)
        )
      }
    />
  );
};

interface UnassignedEmployeeTriggerProps {
  onPress: () => void;
  style?: StyleProp<ViewStyle>;
}

// Shared default look for the unassigned-state trigger -- reps who can hand
// work to a colleague get the generic label, everyone else only ever
// self-assigns here, so say that plainly.
export const UnassignedEmployeeTrigger: React.FC<
  UnassignedEmployeeTriggerProps
> = ({ onPress, style }) => {
  const { smartOrderSettings } = useAuthenticatedUserContext();
  const labelKey = smartOrderSettings?.assign_to_others
    ? 'employee_assignment.assign'
    : 'employee_assignment.assign_to_me';

  return (
    <TouchableOpacity onPress={onPress} style={style}>
      <Typography variant="label-md">{i18n.t(labelKey)}</Typography>
    </TouchableOpacity>
  );
};
